import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { sendEmail, emailTemplates, ADMIN_EMAIL } from '../../lib/emailService';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { DELIVERY_FEE, FREE_DELIVERY_THRESHOLD, TAX_RATE } from '../../lib/pricing';

// Load Stripe outside of component render to avoid recreating on each render
const stripePromise = loadStripe('pk_test_51TUGHHASz0LSofnGLW8swAbmUo6rGQksyIlbkzCfR9ZVeQMD3PhhkIzE9EP1fks54lyEXnzYvVyWIwQEKcADfBEw00JZtoal3u');

// Format military time to 12-hour format
function formatTime(militaryTime) {
  if (!militaryTime) return '';
  const [hours, minutes] = militaryTime.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

// Format date string to human-readable format
function formatHumanDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
}



// ─── Stripe Payment Form ───────────────────────────────────────────────────
function PaymentForm({ onSuccess, onBack, isSubmitting, setIsSubmitting, setErrorMsg, orderPayload, session, grandTotal, taxAmount, deliveryCharge, formData }) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      // Confirm the payment — card is charged immediately
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Payment authorized — now create the account + order in Supabase
      await onSuccess();

    } catch (err) {
      console.error('Payment error:', err);
      setErrorMsg(err.message || 'Payment failed. Please check your card details and try again.');
      setIsSubmitting(false);
    }
  };

  const stripeAppearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#e8a4b8',
      colorBackground: '#ffffff',
      colorText: '#2c3e38',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, sans-serif',
      borderRadius: '16px',
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        border: '1px solid #e3e8e1',
        boxShadow: 'none',
        padding: '12px 16px',
      },
      '.Input:focus': {
        border: '1px solid #e8a4b8',
        boxShadow: '0 0 0 3px rgba(232, 164, 184, 0.15)',
      },
      '.Label': {
        fontWeight: '600',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#6b8262',
      },
    },
  };

  return (
    <form onSubmit={handlePay} className="space-y-6">
      {/* Order summary */}
      <div className="bg-sage-50/50 rounded-2xl p-5 border border-sage-100 space-y-3">
        <h4 className="font-serif text-lg text-charcoal">Order Summary</h4>
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">{formData.product?.name || 'Order'}</span>
          <span className="font-bold text-charcoal">${grandTotal.toFixed(2)}</span>
        </div>
        {(formData.addons || []).map(addon => (
          <div key={addon.id} className="flex justify-between text-sm">
            <span className="text-sage-500">{addon.name}</span>
            <span className="font-bold text-charcoal">${addon.price}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">Tax (9.1% AZ TPT)</span>
          <span className="font-bold text-charcoal">${taxAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-sage-500">Delivery</span>
          <span className="font-bold">
            {deliveryCharge === 0
              ? <span className="text-green-600">FREE</span>
              : <span className="text-charcoal">${DELIVERY_FEE}</span>}
          </span>
        </div>
        <div className="flex justify-between pt-3 border-t border-sage-100">
          <span className="font-serif text-lg text-charcoal">Total</span>
          <span className="font-serif text-2xl text-charcoal">${grandTotal.toFixed(2)}</span>
        </div>
        <p className="text-[10px] text-sage-400 font-medium">
          Your card will be charged when you confirm the order.
        </p>
      </div>

      {/* Stripe payment element */}
      <div className="bg-white rounded-2xl border border-sage-100 p-5">
        <PaymentElement />
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between gap-4 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="btn-secondary md:w-auto min-h-[56px] text-lg"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !stripe}
          className="btn-primary md:w-auto md:px-12 flex items-center justify-center gap-3 min-h-[56px] text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Processing...
            </>
          ) : (
            <>
              <SafeIcon name="Lock" />
              Pay ${grandTotal.toFixed(2)}
            </>
          )}
        </button>
      </div>

      <p className="text-center text-[10px] text-sage-400 font-medium flex items-center justify-center gap-1">
        <SafeIcon name="Lock" className="text-xs" />
        Secured by Stripe. Your card details are never stored on our servers.
      </p>
    </form>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function Step4Review({ formData, onBack, setStep, session, onOrderComplete, corporateClient, orderRules = {} }) {
  const [subStep, setSubStep] = useState('review'); // 'review' | 'payment' | 'split_sent'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);

  // Corporate-mode state
  const groupSize = corporateClient ? (formData.group_size || 1) : 1;
  const [paymentMode, setPaymentMode] = useState('together'); // 'together' | 'split'
  const [payers, setPayers] = useState([]);
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  // Build initial payer rows whenever groupSize or session changes
  useEffect(() => {
    if (!corporateClient) return;
    setPayers(prev => {
      const rows = Array.from({ length: groupSize }, (_, i) => ({
        name: prev[i]?.name ?? (i === 0 ? (formData.customer_name || session?.user?.email || '') : ''),
        email: prev[i]?.email ?? (i === 0 ? (formData.customer_email || session?.user?.email || '') : ''),
      }));
      return rows;
    });
  }, [groupSize, corporateClient]);

  const isLoggedIn  = !!session;
  const productName = formData.product?.name || 'Order';
  const isMealPrep  = formData.delivery_type === 'meal_prep';
  const isMeetUp    = formData.delivery_type === 'meet_up';
  const isPortioned = isMealPrep || isMeetUp;

  // Effective quantity: corporate uses group_size, portioned (meal_prep/meet_up) uses formData.quantity, regular = 1
  const portionedQty = isPortioned ? (formData.quantity || 1) : 1;
  const effectiveQty = corporateClient ? groupSize : portionedQty;

  // Pricing — addons are per-order for portioned types, per-person for corporate/regular
  const addonsTotal       = (formData.addons || []).reduce((sum, a) => sum + (a.price * (a.qty || 1)), 0);
  const unitPrice         = formData.size?.price || formData.product?.base_price || 0;
  const perPersonSubtotal = isPortioned ? unitPrice : (unitPrice + addonsTotal);
  const groupSubtotal     = isPortioned
    ? (unitPrice * effectiveQty) + addonsTotal
    : perPersonSubtotal * effectiveQty;

  // Meet-up: no delivery fee + optional admin-configured discount
  const meetUpDiscountPct  = isMeetUp ? (orderRules.meet_up_discount_pct ?? 0) : 0;
  const meetUpDiscount     = isMeetUp ? Math.round(groupSubtotal * meetUpDiscountPct / 100 * 100) / 100 : 0;
  const discountedSubtotal = groupSubtotal - meetUpDiscount;

  const taxAmount      = Math.round(discountedSubtotal * TAX_RATE * 100) / 100;
  const deliveryCharge = (isMeetUp || discountedSubtotal >= FREE_DELIVERY_THRESHOLD) ? 0 : DELIVERY_FEE;
  const grandTotal     = Math.round((discountedSubtotal + taxAmount + deliveryCharge) * 100) / 100;

  // Per-person split amount (cents, rounded up to avoid rounding loss)
  const perPersonCents = Math.ceil((grandTotal / groupSize) * 100);

  const subtotal = groupSubtotal; // alias kept for clarity below

  // When customer clicks "Continue to Payment" on the review screen,
  // create the account first then get the PaymentIntent client secret
  const handleProceedToPayment = async () => {
    setLoadingPayment(true);
    setErrorMsg('');
    setAlreadyRegistered(false);

    try {
      let authUserId = null;

      if (isSupabaseConfigured()) {
        if (isLoggedIn) {
          authUserId = session.user.id;
          await supabase.from('profiles').update({
            full_name: formData.customer_name,
            phone: formData.customer_phone,
          }).eq('id', authUserId);

        } else {
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: formData.customer_email,
            password: formData.password,
            options: { data: { full_name: formData.customer_name } }
          });

          if (authError) {
            if (
              authError.message?.toLowerCase().includes('already registered') ||
              authError.message?.toLowerCase().includes('already been registered') ||
              authError.status === 422
            ) {
              setAlreadyRegistered(true);
              setLoadingPayment(false);
              return;
            }
            throw authError;
          }

          authUserId = authData.user?.id;
          if (!authUserId) throw new Error('Account creation failed. Please try again.');

          const { error: profileError } = await supabase.from('profiles').upsert({
            id: authUserId,
            full_name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone,
          });

          if (profileError) throw new Error('Failed to create your account. Please try again.');
        }

        // Create PaymentIntent via Edge Function — capture immediately on confirm
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: {
            amount: grandTotal,
            customerEmail: formData.customer_email,
            customerName: formData.customer_name,
            description: `${productName} - Phoenix Kabob`,
            capture_method: 'automatic', // Charge card immediately on confirm
          }
        });

        if (error) throw new Error(error.message || 'Failed to initialize payment.');
        if (!data?.clientSecret) throw new Error('No client secret returned from payment service.');

        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);

        // Store authUserId for use after payment
        formData._authUserId = authUserId;

        setSubStep('payment');

      } else {
        // Mock mode — skip to payment UI
        setClientSecret('mock_secret');
        setSubStep('payment');
      }

    } catch (err) {
      console.error('Proceed to payment error:', err);
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  // Called after Stripe confirms the card authorization
  const handlePaymentSuccess = async () => {
    setIsSubmitting(true);
    try {
      const authUserId = formData._authUserId || session?.user?.id;

      // Insert Order with payment intent ID and paid status
      const { data: newOrder, error: orderError } = await supabase
        .from('orders')
        .insert({
          customer_id: authUserId,
          customer_name: formData.customer_name,
          customer_email: formData.customer_email,
          customer_phone: formData.customer_phone,
          occasion: formData.occasion,
          delivery_date: formData.delivery_date,
          delivery_type: formData.delivery_type || 'scheduled',
          delivery_address: formData.delivery_address,
          delivery_city: formData.delivery_city,
          delivery_zip: formData.delivery_zip,
          recipient_name: formData.recipient_name,
          recipient_phone: formData.recipient_phone,
          special_instructions: formData.special_instructions,
          stripe_payment_intent_id: paymentIntentId,
          stripe_amount_cents: Math.round(grandTotal * 100),
          tax_amount_cents: Math.round(taxAmount * 100),
          meet_up_event_id: formData.meet_up_event_id || null,
          status: 'paid',
        })
        .select()
        .single();

      if (orderError) throw orderError;
      setOrderId(newOrder.id);

      // Insert Order Item
      const { data: newItem, error: itemError } = await supabase
        .from('order_items')
        .insert({
          order_id: newOrder.id,
          product_name: productName,
          product_image_url: formData.product?.images?.[0] || null,
          size: formData.size?.name || null,
          quantity: effectiveQty,
          price: formData.size?.price || formData.product?.base_price,
          addons: formData.addons?.length > 0 ? JSON.stringify(formData.addons) : null,
        })
        .select()
        .single();

      if (itemError) throw itemError;

      // Send emails fire-and-forget — never block the success screen
      sendEmail(
        formData.customer_email,
        "Your order is confirmed — Phoenix Kabob",
        emailTemplates.orderReceived(newOrder, newItem)
      ).catch(err => console.error('Customer confirmation email failed:', err));

      sendEmail(
        ADMIN_EMAIL,
        `New Order — #${newOrder.id.substring(0, 8)} from ${formData.customer_name}`,
        emailTemplates.adminNewOrder(newOrder, newItem)
      ).catch(err => console.error('Admin notification failed:', err));

      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#e8a4b8', '#c7607e', '#6b8262', '#ffffff']
      });
      if (onOrderComplete) onOrderComplete();
      setIsSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 9000);

    } catch (err) {
      console.error('Order creation error:', err);
      setErrorMsg(err.message || 'Order could not be saved. Please contact us at orders@phoenixkabob.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Corporate split payment ────────────────────────────────────────────
  const handleSplitPayment = async () => {
    setErrorMsg('');
    const invalid = payers.some((p, i) => !p.name.trim() || !p.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(p.email));
    if (invalid) { setErrorMsg('Please fill in a valid name and email for every person.'); return; }

    setSplitSubmitting(true);
    try {
      const coordinatorId = session?.user?.id;
      if (!coordinatorId) throw new Error('You must be signed in to place a group order.');

      // 1. Create the group_orders parent record
      const { data: groupOrder, error: goError } = await supabase
        .from('group_orders')
        .insert({
          corporate_client_id: corporateClient.id,
          coordinator_id: coordinatorId,
          delivery_date: formData.delivery_date,
          delivery_time: formData.delivery_time || null,
          delivery_address: formData.delivery_address,
          special_instructions: formData.special_instructions || null,
          payment_mode: 'split',
          status: 'awaiting_payment',
        })
        .select()
        .single();
      if (goError) throw new Error(goError.message);

      // 2. Insert one group_order_items row per payer
      const itemRows = payers.map(p => ({
        group_order_id: groupOrder.id,
        company_name: corporateClient.name,
        recipient_name: p.name,
        recipient_email: p.email,
        product_name: productName,
        addons: formData.addons || [],
        item_total_cents: perPersonCents,
        payment_status: 'unpaid',
      }));

      const { data: items, error: itemsError } = await supabase
        .from('group_order_items')
        .insert(itemRows)
        .select();
      if (itemsError) throw new Error(itemsError.message);

      // 3. Call create-checkout-sessions to get Stripe Checkout URLs
      const payerPayload = items.map((item, i) => ({
        itemId: item.id,
        name: payers[i].name,
        email: payers[i].email,
        amountCents: perPersonCents,
        companyName: corporateClient.name,
      }));

      const { data: sessData, error: sessError } = await supabase.functions.invoke('create-checkout-sessions', {
        body: { groupOrderId: groupOrder.id, payers: payerPayload }
      });
      if (sessError) throw new Error(sessError.message || 'Failed to create payment links.');
      if (sessData?.error) throw new Error(sessData.error);

      if (onOrderComplete) onOrderComplete();
      setSubStep('split_sent');

    } catch (err) {
      console.error('Split payment error:', err);
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSplitSubmitting(false);
    }
  };

  // ── Success Screen ─────────────────────────────────────────────────────
  if (isSuccess) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 space-y-6">
        <div className="w-24 h-24 bg-blush-100 rounded-full flex items-center justify-center mx-auto">
          <SafeIcon name="Check" className="text-5xl text-blush-400" />
        </div>
        <h2 className="text-4xl font-serif text-charcoal">Order Placed!</h2>
        <div className="space-y-1">
          <p className="text-sage-500 text-lg">Thank you, {formData.customer_name?.split(' ')[0]}.</p>
          <p className="text-blush-400 font-bold tracking-widest text-sm">ORDER ID: {orderId.substring(0, 8)}</p>
        </div>
        <div className="glass-panel p-6 max-w-sm mx-auto text-left space-y-4 mt-4">
          <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-center gap-3">
            <SafeIcon name="CheckCircle" className="text-green-500 shrink-0 text-xl" />
            <div>
              <p className="text-sm font-bold text-green-700">Payment confirmed</p>
              <p className="text-xs text-green-600">Your card has been charged ${grandTotal.toFixed(2)}. A confirmation email is on its way.</p>
            </div>
          </div>
          <h3 className="font-serif text-lg border-b border-sage-100 pb-2">What happens next?</h3>
          <ul className="space-y-3 text-sm text-charcoal">
            <li className="flex gap-3">
              <SafeIcon name="Camera" className="text-blush-300 shrink-0 mt-0.5" />
              We'll craft your order fresh from our home kitchen and send you a photo when it's ready.
            </li>
            <li className="flex gap-3">
              {isMeetUp ? (
                <SafeIcon name="MapPin" className="text-blush-300 shrink-0 mt-0.5" />
              ) : (
                <SafeIcon name="Truck" className="text-blush-300 shrink-0 mt-0.5" />
              )}
              {isMeetUp
                ? `Pick up your order at ${formData.meet_up_location || 'the meet-up location'} on your chosen date.`
                : 'We hand deliver on your chosen date.'}
            </li>
          </ul>
          <button
            onClick={() => window.location.hash = '#/portal'}
            className="btn-primary w-full flex items-center justify-center gap-3 mt-4 shadow-glow"
          >
            Track Your Order
            <SafeIcon name="ArrowRight" />
          </button>
        </div>
      </motion.div>
    );
  }

  // ── Split payment sent screen ──────────────────────────────────────────
  if (subStep === 'split_sent') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 space-y-6">
        <div className="w-24 h-24 bg-blush-100 rounded-full flex items-center justify-center mx-auto">
          <SafeIcon name="Send" className="text-5xl text-blush-400" />
        </div>
        <h2 className="text-4xl font-serif text-charcoal">Payment Links Sent!</h2>
        <p className="text-sage-500 text-lg max-w-sm mx-auto">
          Each person has received an email with their payment link. The order will be confirmed once everyone pays.
        </p>
        <div className="glass-panel p-6 max-w-sm mx-auto text-left space-y-3">
          <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">What happens next?</p>
          <ul className="space-y-3 text-sm text-charcoal">
            <li className="flex gap-3">
              <SafeIcon name="Mail" className="text-blush-300 shrink-0 mt-0.5" />
              Each person got an email with their secure payment link (expires in 24 hours).
            </li>
            <li className="flex gap-3">
              <SafeIcon name="CheckCircle" className="text-blush-300 shrink-0 mt-0.5" />
              Once all {groupSize} people pay, we'll start preparing your order.
            </li>
          </ul>
        </div>
      </motion.div>
    );
  }

  // ── Payment Step ───────────────────────────────────────────────────────
  if (subStep === 'payment' && clientSecret && clientSecret !== 'mock_secret') {
    return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-serif text-charcoal">Secure Payment</h2>
          <p className="text-sage-500">Your card will be charged when you place your order.</p>
        </div>

        {errorMsg && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm text-center">
            {errorMsg}
          </div>
        )}

        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#e8a4b8',
                colorBackground: '#ffffff',
                colorText: '#2c3e38',
                fontFamily: 'Inter, sans-serif',
                borderRadius: '16px',
              },
              rules: {
                '.Input': { border: '1px solid #e3e8e1', padding: '12px 16px' },
                '.Input:focus': { border: '1px solid #e8a4b8', boxShadow: '0 0 0 3px rgba(232,164,184,0.15)' },
                '.Label': { fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b8262' },
              }
            }
          }}
        >
          <PaymentForm
            onSuccess={handlePaymentSuccess}
            onBack={() => setSubStep('review')}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            setErrorMsg={setErrorMsg}
            grandTotal={grandTotal}
            taxAmount={taxAmount}
            deliveryCharge={deliveryCharge}
            formData={formData}
          />
        </Elements>
      </motion.div>
    );
  }

  // ── Review Step ────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-serif text-charcoal">Review Your Order</h2>
        <p className="text-sage-500">Double check the details before we get to work.</p>
      </div>

      {alreadyRegistered && (
        <div className="bg-blush-50 border border-blush-100 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <SafeIcon name="AlertCircle" className="text-blush-400 text-xl shrink-0" />
            <p className="text-sm font-semibold text-charcoal">An account with this email already exists.</p>
          </div>
          <p className="text-sm text-sage-500 pl-8">
            Please{' '}
            <a href="#/portal" className="text-blush-400 font-bold hover:text-blush-500 transition-colors">
              sign in to your account
            </a>
            {' '}to place a new order, or use a different email address.
          </p>
        </div>
      )}

      {errorMsg && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm text-center">
          {errorMsg}
        </div>
      )}

      <div className="glass-panel overflow-hidden">

        {/* ── Hero: order summary ── */}
        <div className="p-6 md:p-8 border-b border-sage-100 bg-sage-50/30">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-blush-50 border border-blush-100 flex items-center justify-center shrink-0 text-3xl">
              🥙
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-serif text-xl text-charcoal">{productName}</h4>
                  {formData.special_requests && (
                    <p className="text-xs text-sage-400 mt-1 italic">"{formData.special_requests}"</p>
                  )}
                </div>
                <button onClick={() => setStep(1)} className="text-sage-400 hover:text-blush-400 text-xs shrink-0 ml-4">Edit</button>
              </div>

              {(formData.addons || []).length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Add-ons</p>
                    <button onClick={() => setStep('addons')} className="text-sage-400 hover:text-blush-400 text-xs">Edit</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(formData.addons || []).map((addon) => (
                      <span key={addon.id} className="bg-white border border-sage-100 text-charcoal px-3 py-1 rounded-full text-xs font-medium">
                        {addon.qty > 1 ? `${addon.qty}× ` : ''}{addon.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom: order total + delivery side by side ── */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Order total */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="font-serif font-medium text-charcoal text-lg">Order Summary</h4>
              <button onClick={() => setStep(1)} className="text-sage-400 hover:text-blush-400 text-sm">Edit</button>
            </div>
            <div className="bg-warmwhite p-5 rounded-2xl border border-sage-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-sage-400">
                  {productName}
                  {effectiveQty > 1 ? ` × ${effectiveQty}${isPortioned ? ' portions' : ''}` : ''}
                </span>
                <span className="font-medium text-charcoal">
                  ${(unitPrice * effectiveQty).toFixed(2)}
                </span>
              </div>
              {(formData.addons || []).map(addon => (
                <div key={addon.id} className="flex justify-between text-sm">
                  <span className="text-sage-400">
                    {addon.qty > 1 ? `${addon.qty}× ` : ''}{addon.name}
                    {!isPortioned && effectiveQty > 1 ? ` × ${effectiveQty}` : ''}
                  </span>
                  <span className="font-medium text-charcoal">
                    ${(addon.price * (addon.qty || 1) * (isPortioned ? 1 : effectiveQty)).toFixed(2)}
                  </span>
                </div>
              ))}
              {meetUpDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Meet-Up Discount ({meetUpDiscountPct}%)</span>
                  <span className="font-medium">−${meetUpDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t border-sage-100 mt-1">
                <span className="text-sage-400">Tax (9.1% AZ TPT)</span>
                <span className="font-medium text-charcoal">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-sage-400">
                  {isMeetUp ? 'Delivery' : `Delivery ${deliveryCharge === 0 ? `(free over $${FREE_DELIVERY_THRESHOLD})` : ''}`}
                </span>
                {isMeetUp ? (
                  <span className="font-medium text-green-600">FREE (pick-up)</span>
                ) : deliveryCharge === 0 ? (
                  <span className="font-medium text-green-600">FREE</span>
                ) : (
                  <span className="font-medium text-charcoal">${deliveryCharge}</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-sage-200 mt-1">
                <span className="font-serif text-lg text-charcoal">Total</span>
                <span className="font-serif text-3xl text-charcoal">${grandTotal.toFixed(2)}</span>
              </div>
              {groupSize > 1 && paymentMode === 'split' && (
                <p className="text-xs text-blush-500 font-semibold pt-1">
                  ${(perPersonCents / 100).toFixed(2)} per person
                </p>
              )}
              <p className="text-[10px] text-sage-400 font-medium pt-1">Charged on confirmation</p>
            </div>
          </div>

          {/* Delivery / Meet-Up details */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="font-serif font-medium text-charcoal text-lg">
                {isMeetUp ? 'Pick-Up Details' : 'Delivery'}
              </h4>
              <button onClick={() => setStep(2)} className="text-sage-400 hover:text-blush-400 text-sm">Edit</button>
            </div>
            <div className="bg-warmwhite p-5 rounded-2xl border border-sage-100 space-y-4">
              {isMeetUp ? (
                <>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Event</p>
                    <p className="font-bold text-charcoal">{formData.meet_up_event_title || 'Sunday Meet-Up'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Date &amp; Time</p>
                    <p className="font-bold text-charcoal">
                      {formData.delivery_date ? formatHumanDate(formData.delivery_date) : '—'}
                    </p>
                    {formData.delivery_time && (
                      <p className="text-sm text-sage-500 mt-1">Starting at {formatTime(formData.delivery_time)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Pick-Up Location</p>
                    <p className="font-bold text-charcoal">{formData.meet_up_location || '—'}</p>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-2.5 flex items-center gap-2">
                    <SafeIcon name="Check" className="text-green-500 text-sm shrink-0" />
                    <p className="text-xs font-bold text-green-700">No delivery fee — pick up at the event</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Recipient</p>
                    <p className="font-bold text-charcoal">{formData.recipient_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Date &amp; Time</p>
                    <p className="font-bold text-charcoal">
                      {formData.delivery_date ? formatHumanDate(formData.delivery_date) : '—'}
                    </p>
                    {formData.delivery_time && (
                      <p className="text-sm text-sage-500 mt-1">
                        {formatTime(formData.delivery_time)} (±30 min window)
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Address</p>
                    <p className="font-bold text-charcoal">{formData.delivery_address}</p>
                    <p className="text-sm text-sage-500">{formData.delivery_city}, {formData.delivery_zip}</p>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── Corporate: payment mode selector ── */}
      {corporateClient && groupSize > 1 && (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-3">How would you like to pay?</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'together', label: 'One card', desc: 'Pay the full amount on your card', icon: 'CreditCard' },
                { id: 'split', label: 'Split it', desc: `Everyone pays ${(perPersonCents / 100).toFixed(2)} via their own link`, icon: 'Users' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPaymentMode(opt.id)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    paymentMode === opt.id
                      ? 'border-blush-300 bg-blush-50/50'
                      : 'border-sage-100 bg-white hover:border-sage-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <SafeIcon name={opt.icon} className={paymentMode === opt.id ? 'text-blush-400' : 'text-sage-400'} />
                    <span className={`font-bold text-sm ${paymentMode === opt.id ? 'text-blush-500' : 'text-charcoal'}`}>{opt.label}</span>
                  </div>
                  <p className="text-xs text-sage-400">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Payer list for split mode */}
          {paymentMode === 'split' && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Enter each person's details</p>
              {payers.map((payer, i) => (
                <div key={i} className="grid grid-cols-2 gap-3 items-start">
                  <div>
                    <label className="block text-xs text-sage-400 mb-1">Name {i === 0 ? '(you)' : `#${i + 1}`}</label>
                    <input
                      type="text"
                      value={payer.name}
                      onChange={e => setPayers(prev => prev.map((p, j) => j === i ? { ...p, name: e.target.value } : p))}
                      placeholder="Full name"
                      className="w-full border border-sage-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-sage-400 mb-1">Email</label>
                    <input
                      type="email"
                      value={payer.email}
                      onChange={e => setPayers(prev => prev.map((p, j) => j === i ? { ...p, email: e.target.value } : p))}
                      placeholder="email@company.com"
                      className="w-full border border-sage-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blush-200"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col-reverse md:flex-row justify-between gap-4 pt-4">
        <button onClick={onBack} disabled={loadingPayment || splitSubmitting} className="btn-secondary md:w-auto min-h-[56px] text-lg">Back</button>

        {corporateClient && paymentMode === 'split' && groupSize > 1 ? (
          <button
            onClick={handleSplitPayment}
            disabled={splitSubmitting}
            className="btn-primary md:w-auto md:px-12 flex items-center justify-center gap-3 min-h-[56px] text-lg"
          >
            {splitSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Sending Links…
              </>
            ) : (
              <>
                <SafeIcon name="Send" />
                Send Payment Links
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleProceedToPayment}
            disabled={loadingPayment}
            className="btn-primary md:w-auto md:px-12 flex items-center justify-center gap-3 min-h-[56px] text-lg"
          >
            {loadingPayment ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Preparing Payment...
              </>
            ) : (
              <>
                Continue to Payment
                <SafeIcon name="ArrowRight" />
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
}
